import React, { useMemo, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  TextField,
  Button,
  Dialog,
  Grid,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import "moment/locale/de";
import BookRow from "./BookRow";
import "./styles.scss";
import TotalRow from "./TotalRow";
import { SettingsDetailsType } from "../../types/genericType";
import { getCloseSummary, runSummary } from "../../services/booksService";
import { currencyFormatter } from "../../utils/utils";

type NewTransactionProps = {
  setOnTxnSuccess: Function;
  setSnackbarMsg: Function;
  handleDrawerClose: Function;
  settingsData: SettingsDetailsType;
};

const CloseBook = (props: NewTransactionProps) => {
  const { handleDrawerClose, setSnackbarMsg, setOnTxnSuccess, settingsData } =
    props;
  const { ManagementRate, VariableInvestmentRate, MonthEndDescription } = settingsData;
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [isRuning, setIsRuning] = useState(false);
  const [formData, setFormData] = useState<{
    managementRate: string | undefined;
    investmentRate: string | undefined;
    description: string | undefined;
    date: Dayjs;
  }>({
    managementRate: ManagementRate || "0.00",
    investmentRate: VariableInvestmentRate || "0.00",
    description: MonthEndDescription,
    date: dayjs().subtract(30, "day"),
  });

  const { managementRate, investmentRate, description, date } = formData;
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleClickOpen = () => {
    setLoading(true);
    getCloseSummary({
      month: date.month() + 1,
      year: date.year(),
      investmentRate: Number(investmentRate),
      managementRate: Number(managementRate),
      description,
    })
      .then((res) => {
        setSummary(res);
        setLoading(false);
        setOpen(true);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleRun = () => {
    setIsRuning(true);
    runSummary({
      month: date.month() + 1,
      year: date.year(),
      investmentRate: Number(investmentRate),
      managementRate: Number(managementRate),
      description,
    })
      .then(() => {
        setOnTxnSuccess(true);
        setSnackbarMsg("Month End Processed Successfully");
        handleClose();
        handleDrawerClose();
        setIsRuning(false);
        window.location.reload(); 
      })
      .catch((err) => {
        console.log(err);
        setIsRuning(false);
      });
  };

  const handleDateChange = (date: any) => {
    setFormData({ ...formData, date: dayjs(date) });
  };

  const total = useMemo(() => {
    let outgoingTotal = 0;
    let incomingTotal = 0;
    if (Object.keys(summary).length > 0) {
      outgoingTotal = summary.investment.due + summary.managementFee.total;
      incomingTotal = summary.fixedLoan.due + summary.variableLoan.due;
    }
    return { outgoingTotal, incomingTotal };
  }, [summary]);

  return (
    <Box className="new-transaction">
      <Stack spacing={3}>
        <Box className="date-picker-container">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              className="date-picker-container"
              slotProps={{ textField: { size: "small" } }}
              value={date}
              onChange={handleDateChange}
              views={["month", "year"]}
              maxDate={dayjs().endOf("month")}
              sx={{
                "& .Mui-selected": {
                  color: "#FFFFFF",
                  borderRadius: 2,
                  borderWidth: 1,
                  borderColor: "#e91e63",
                  border: "1px solid",
                  backgroundColor: "#f8bbd0",
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <TextField
          type="number"
          id="outlined-helperText"
          label="Management Rate"
          value={managementRate}
          name="managementRate"
          onChange={handleChange}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          size="small"
        />
        <TextField
          type="number"
          id="outlined-helperText"
          label="Variable Investment Rate:"
          value={investmentRate}
          name="investmentRate"
          onChange={handleChange}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          size="small"
        />
        <TextField
          id="outlined-helperText"
          label="Description"
          value={description}
          name="description"
          onChange={handleChange}
          size="small"
        />
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            size="small"
            className="create-button"
            onClick={handleClickOpen}
            disabled={loading}
          >
            View Summary
          </Button>
          <Button className="cancel-button" onClick={() => handleDrawerClose()}>
            CANCEL
          </Button>
        </Stack>
      </Stack>
      {Object.keys(summary).length > 0 && (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
          <Grid container spacing={2}>
            <Grid item xs={3} className="summary-left">
              <Box p={3}>
                <Box py={2}>
                  <Typography variant="h5" component="h2">{`${dayjs(
                    date
                  ).format("MMMM YYYY")}`}</Typography>
                </Box>
                <Divider />
                <Box py={2}>
                  <Typography variant="subtitle1" component="h3">
                    Total Outgoing
                  </Typography>
                  <Typography variant="body1" component="h3">
                    {currencyFormatter.format(total.outgoingTotal)}
                  </Typography>
                </Box>
                <Divider />
                <Box py={2}>
                  <Typography variant="subtitle1" component="h3">
                    Total Incoming
                  </Typography>
                  <Typography variant="body1" component="h3">
                    {currencyFormatter.format(total.incomingTotal)}
                  </Typography>
                </Box>
                <Divider />
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  py={2}
                >
                  <Button className="cancel-button" onClick={handleClose}>
                    CANCEL
                  </Button>
                  <LoadingButton variant="contained" size="large" onClick={handleRun} loading={isRuning}>
                    Run
                  </LoadingButton>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box className="summary-right">
                <Typography variant="h6" component="h2">
                  Process Summary{" "}
                </Typography>
                <IconButton className="close-btn" onClick={handleClose}>
                  <Close />
                </IconButton>
                <Grid container spacing={8} pt={3}>
                  <BookRow
                    title="Investment"
                    data={[
                      {
                        title: "Average Balance",
                        value: currencyFormatter.format(
                          summary.investment.balance
                        ),
                      },
                      {
                        title: "Interest Rate",
                        value: `${summary.investment.rate}%`,
                      },
                      {
                        title: "Interest Due",
                        value: currencyFormatter.format(summary.investment.due),
                      },
                    ]}
                  />
                  <BookRow
                    title="Fixed Loan"
                    data={[
                      {
                        title: "Average Balance",
                        value: currencyFormatter.format(
                          summary.fixedLoan.balance
                        ),
                      },
                      {
                        title: "Interest",
                        value: currencyFormatter.format(summary.fixedLoan.due),
                      },
                    ]}
                  />

                  <BookRow
                    title="Management"
                    data={[
                      {
                        title: "Fee",
                        value: currencyFormatter.format(
                          summary.managementFee.fee
                        ),
                      },
                      {
                        title: "Rate",
                        value: `${summary.managementFee.rate}%`,
                      },
                      {
                        title: "Offset",
                        value: currencyFormatter.format(
                          summary.managementFee.offset
                        ),
                      },
                      {
                        title: "Total Management Fee",
                        value: currencyFormatter.format(
                          summary.managementFee.total
                        ),
                      },
                    ]}
                  />
                  <BookRow
                    title="Variable Investment"
                    data={[
                      {
                        title: "Average Balance",
                        value: currencyFormatter.format(
                          summary.variableLoan.balance
                        ),
                      },
                      {
                        title: "Interest",
                        value: currencyFormatter.format(
                          summary.variableLoan.due
                        ),
                      },
                      {
                        title: "Rate",
                        value: `${summary.variableLoan.rate}%`,
                      },
                    ]}
                  />
                  <TotalRow
                    title="Total Outgoing"
                    value={currencyFormatter.format(total.outgoingTotal)}
                  />
                  <TotalRow
                    title=" Total Incoming"
                    value={currencyFormatter.format(total.incomingTotal)}
                  />
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Dialog>
      )}
    </Box>
  );
};

export default CloseBook;
